package org.gymly.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

import org.gymly.data.PortalRepo
import org.gymly.esAndroid
import org.gymly.model.MarketingItem
import org.gymly.model.PortalHome
import org.gymly.model.PortalTab
import org.gymly.model.PortalUser
import org.gymly.theme.GymlyColors
import org.gymly.theme.GymlyFont
import org.gymly.theme.GymlySpace
import org.gymly.theme.TileImages
import org.gymly.ui.components.GymlyLoadingCenter
import org.gymly.ui.components.HomeTile
import org.gymly.ui.components.MarketingCarousel
import org.gymly.ui.components.MobileBlueHeader
import org.gymly.ui.components.SummaryCard
import org.gymly.ui.components.homeTileIcon
import org.gymly.util.formatMoney
import org.gymly.util.formatTime
import org.gymly.util.isoWeekdayOf
import org.gymly.util.todayIsoDate
import org.gymly.util.userMessage

private const val MARKETING_REFRESH_MS = 60_000L

/** Altura fija de las tarjetas del resumen: cabe una página de 5 filas. */
private const val SUMMARY_PAGE_SIZE = 5

@Composable
fun HomeScreen(
    user: PortalUser,
    onLogout: () -> Unit,
    onNavigate: (PortalTab) -> Unit,
    mobile: Boolean = false,
    unreadCount: Long = 0,
    langEs: Boolean = true,
    onLangEs: (Boolean) -> Unit = {},
    onBell: () -> Unit = {},
) {
    var home by remember { mutableStateOf<PortalHome?>(null) }
    var marketing by remember { mutableStateOf<List<MarketingItem>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    fun load() {
        scope.launch {
            loading = true
            error = null
            try {
                home = PortalRepo.api.fetchHome()
                marketing = PortalRepo.api.fetchMarketing()
            } catch (e: Exception) {
                error = e.userMessage("Sin conexión o error del servidor.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(user) { load() }

    // La publicidad nueva aparece sola: se vuelve a consultar cada minuto, sin recargar el resto del inicio.
    LaunchedEffect(user) {
        while (true) {
            delay(MARKETING_REFRESH_MS)
            runCatching { PortalRepo.api.fetchMarketing() }.onSuccess { marketing = it }
        }
    }

    val displayName = home?.profile?.displayName?.ifBlank { null }
        ?: home?.profile?.let { "${it.firstName} ${it.lastName}".trim() }
        ?: user.displayName
    val firstName = displayName.split(" ").firstOrNull().orEmpty()

    BoxWithConstraints(Modifier.fillMaxSize()) {
        val pageWidth = maxWidth

        Column(Modifier.fillMaxSize()) {
            if (mobile) {
                MobileBlueHeader(
                    greeting = if (firstName.isNotBlank()) "Hola, $firstName" else "Hola",
                    subtitle = "Tu gimnasio de un vistazo",
                    unreadCount = unreadCount,
                    langEs = langEs,
                    onLangEs = onLangEs,
                    onBell = onBell,
                )
            } else {
                Row(
                    Modifier
                        .fillMaxWidth()
                        .padding(horizontal = GymlySpace.md, vertical = GymlySpace.sm),
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Column {
                        Text(
                            "Hola${firstName.takeIf { it.isNotBlank() }?.let { ", $it" } ?: ""}",
                            fontSize = GymlyFont.title,
                            fontWeight = FontWeight.Bold,
                        )
                        Text("Tu gimnasio en un vistazo", color = GymlyColors.TextMuted)
                    }
                    TextButton(onClick = {
                        PortalRepo.api.logout()
                        onLogout()
                    }) {
                        Text("Salir", color = GymlyColors.Primary, fontWeight = FontWeight.SemiBold)
                    }
                }
            }

            Column(
                Modifier
                    .weight(1f)
                    .verticalScroll(rememberScrollState()),
            ) {
                Spacer(Modifier.height(GymlySpace.md))
                // Mosaico de accesos encima del resumen (como antes). Pares en filas; si queda uno suelto, ocupa todo el ancho.
                // Solo se dibuja cuando ya se conoce la configuración: así "Reservas" no aparece y desaparece al cargar.
                if (home != null) Column(Modifier.padding(horizontal = GymlySpace.md)) {
                    val gap = 12.dp
                    val tileW = (pageWidth - GymlySpace.md * 2 - gap) / 2
                    val tiles = buildList {
                        add(Triple("Perfil", TileImages.profile, PortalTab.Profile))
                        add(Triple("Pagos", TileImages.payments, PortalTab.Payments))
                        add(Triple("Tienda", TileImages.payments, PortalTab.Store))
                        add(Triple("Horarios", TileImages.schedules, PortalTab.Schedules))
                        if (home?.schedulesAutomatic != true) {
                            add(Triple("Reservas", TileImages.bookings, PortalTab.Bookings))
                        }
                    }
                    tiles.chunked(2).forEachIndexed { index, row ->
                        if (index > 0) Spacer(Modifier.height(gap))
                        if (row.size == 1) {
                            val (label, image, tab) = row.first()
                            HomeTile(label, image, homeTileIcon(if (tab == PortalTab.Store) "Pagos" else label), pageWidth - GymlySpace.md * 2) {
                                onNavigate(tab)
                            }
                        } else {
                            Row(horizontalArrangement = Arrangement.spacedBy(gap)) {
                                row.forEach { (label, image, tab) ->
                                    HomeTile(label, image, homeTileIcon(if (tab == PortalTab.Store) "Pagos" else label), tileW) {
                                        onNavigate(tab)
                                    }
                                }
                            }
                        }
                    }
                }
                Spacer(Modifier.height(GymlySpace.md))
                when {
                    loading -> GymlyLoadingCenter()
                    error != null -> TextButton(
                        onClick = { load() },
                        modifier = Modifier.padding(GymlySpace.md),
                    ) {
                        Column {
                            Text(error!!, color = GymlyColors.Text)
                            Text("Reintentar", color = GymlyColors.Primary, fontWeight = FontWeight.SemiBold)
                        }
                    }
                    home != null -> {
                        val data = home!!
                        Column(Modifier.padding(horizontal = GymlySpace.md, vertical = GymlySpace.lg)) {
                            Text("Resumen", fontWeight = FontWeight.Bold, fontSize = GymlyFont.body)
                            if (!mobile) {
                                Text(
                                    if (data.schedulesAutomatic) {
                                        "Últimos movimientos y el horario de hoy."
                                    } else {
                                        "Últimos movimientos y tus próximas clases reservadas."
                                    },
                                    color = GymlyColors.TextMuted,
                                    fontSize = GymlyFont.small,
                                    modifier = Modifier.padding(bottom = GymlySpace.md),
                                )
                            } else {
                                Spacer(Modifier.height(10.dp))
                            }
                            // Altura según el contenido: sin hueco cuando hay pocos datos; las tres tarjetas quedan iguales.
                            // En celular las tarjetas se apilan a todo el ancho; en pantallas anchas van en fila.
                            val tarjetas: @Composable (Modifier) -> Unit = { tarjeta ->
                                SummaryCard("Últimos pagos", tarjeta, null) {
                                    SummaryPagedList(homeMoneyRows(data), "Sin pagos recientes.") { row ->
                                        Text(row.amount, fontWeight = FontWeight.SemiBold, maxLines = 1)
                                        Text(
                                            row.detail,
                                            color = GymlyColors.TextMuted,
                                            fontSize = 12.sp,
                                            maxLines = 1,
                                        )
                                    }
                                }
                                if (data.schedulesAutomatic) {
                                    val todayWeekday = isoWeekdayOf(data.today.ifBlank { todayIsoDate() })
                                    val todayClasses = data.weekSchedules
                                        .filter { it.weekday == todayWeekday }
                                        .sortedBy { it.startTime }
                                    // Un entrenador con varios horarios ese día: se muestra una vez con todas sus franjas.
                                    val trainerDays = todayClasses
                                        .groupBy { it.trainerName ?: it.title }
                                        .map { (name, list) ->
                                            TrainerDay(name, list.map { "${formatTime(it.startTime)} – ${formatTime(it.endTime)}" })
                                        }
                                    SummaryCard("Horario de hoy", tarjeta, null) {
                                        SummaryPagedList(trainerDays, "Sin clases hoy.") { day ->
                                            Text(day.name, fontWeight = FontWeight.SemiBold, maxLines = 2)
                                            Text(
                                                day.times.joinToString(" · "),
                                                color = GymlyColors.TextMuted,
                                                fontSize = 12.sp,
                                                maxLines = 3,
                                            )
                                        }
                                    }
                                } else {
                                    SummaryCard("Próximas clases", tarjeta, null) {
                                        SummaryPagedList(data.upcomingClasses, "Sin reservas.") { booking ->
                                            Text(booking.title, fontWeight = FontWeight.SemiBold, maxLines = 1)
                                            Text(
                                                "${booking.sessionDate} · ${formatTime(booking.startTime)}",
                                                color = GymlyColors.TextMuted,
                                                fontSize = 12.sp,
                                                maxLines = 1,
                                            )
                                        }
                                    }
                                }
                            }
                            if (mobile && esAndroid()) {
                                Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                                    tarjetas(Modifier.fillMaxWidth())
                                }
                            } else {
                                Row(Modifier.height(IntrinsicSize.Min), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                                    tarjetas(Modifier.weight(1f))
                                }
                            }
                        }
                        MarketingCarousel(marketing, pageWidth)
                    }
                }

                Spacer(Modifier.height(GymlySpace.xl))
            }
        }
    }
}

private data class HomeMoneyRow(val date: String, val amount: String, val detail: String)

private data class TrainerDay(val name: String, val times: List<String>)

/** Pagos de membresía y compras de tienda, del más reciente al más antiguo. */
private fun homeMoneyRows(data: PortalHome): List<HomeMoneyRow> =
    (
        data.recentPayments.map {
            HomeMoneyRow(it.paymentDate, formatMoney(it.amountCents, it.currency), "${it.methodLabel} · ${it.statusLabel}")
        } + data.recentStoreOrders.map {
            HomeMoneyRow(
                it.saleDate,
                formatMoney(it.totalCents.toInt(), it.currency),
                "Tienda · ${it.methodLabel} · ${it.statusLabel}",
            )
        }
        ).sortedByDescending { it.date }

/** Lista de 5 filas por página con Anterior/Siguiente; vacía muestra [emptyText]. */
@Composable
private fun <T> SummaryPagedList(
    items: List<T>,
    emptyText: String,
    row: @Composable (T) -> Unit,
) {
    if (items.isEmpty()) {
        Text(emptyText, color = GymlyColors.TextMuted, fontSize = GymlyFont.small)
        return
    }
    var page by remember(items) { mutableStateOf(0) }
    val pageCount = (items.size + SUMMARY_PAGE_SIZE - 1) / SUMMARY_PAGE_SIZE
    val current = page.coerceIn(0, pageCount - 1)
    items.drop(current * SUMMARY_PAGE_SIZE).take(SUMMARY_PAGE_SIZE).forEach { item ->
        row(item)
        Spacer(Modifier.height(GymlySpace.sm))
    }
    if (pageCount > 1) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            TextButton(onClick = { page = current - 1 }, enabled = current > 0) {
                Text("Anterior", fontSize = GymlyFont.small)
            }
            Text(
                "${current + 1} / $pageCount",
                color = GymlyColors.TextMuted,
                fontSize = GymlyFont.small,
                modifier = Modifier.weight(1f),
            )
            TextButton(onClick = { page = current + 1 }, enabled = current < pageCount - 1) {
                Text("Siguiente", fontSize = GymlyFont.small)
            }
        }
    }
}
